import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { usePermissions } from '../../context/PermissionsContext';
import { resolveJdPreviewReturnTarget } from '../../utils/jdPreviewNavigation';

import { normalizeJdExperienceZeroToEntryLevel } from '../../utils/jdNormalizeExperience';
import {
  formatJdPreviewHtml,
  normalizeJdForStorage,
  isJdRichTextContent,
  isJdMetaLine,
  JD_PREVIEW_INNER_CSS,
} from '../../utils/jdPreviewFormat';
import { getJdSourceTypeLabel } from '../../utils/jdSourceType';
import {
  loadFeujiLogoData,
  renderJdRichHtmlToPdf,
  renderJdToDocxBlob,
} from '../../utils/feujiDocumentBranding';


const JdPreview = ({ asModal = false, onClose, onSaved, positionName: positionNameProp } = {}) => {
  const perms = usePermissions();
  const exportBlocked = () => {
    if (perms.can('data.export')) return false;
    toast.warn("You don't have permission to download job descriptions.");
    return true;
  };
  const { positionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const displayPositionName = positionNameProp || location.state?.positionName;
  const isProduction = process.env.NODE_ENV === 'production';
  const debugLog = (...args) => {
    if (!isProduction) console.log(...args);
  };
  const [loading, setLoading] = useState(true);
  const [jdData, setJdData] = useState(null);
  const [editedJd, setEditedJd] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingAiJd, setViewingAiJd] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null); // Track which version is being viewed
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const logoDataRef = useRef(null);

  useEffect(() => {
    loadFeujiLogoData().then((data) => {
      if (data) logoDataRef.current = data;
    });
  }, []);

  const formatJdText = (text) => {
    if (!text || typeof text !== 'string') return '';
    if (isJdRichTextContent(text)) {
      return normalizeJdForStorage(text);
    }
    const out = text
      .replace(/^["']|["']$/g, '')
      .replace(/\\n/g, '\n')
      .replace(
        /^\s*0\s*(?:years?|yrs?)\s+in\s+(.+?)\s+or related field.*$/gim,
        'Entry-level candidate with foundational knowledge in $1 or related field, with exposure through academics, internships, or projects.'
      )
      .replace(/^Page \d+ of \d+\s*\n?/gm, '')
      .replace(/Page \d+ of \d+/g, '')
      .replace(/^\s*Page \d+ of \d+\s*$/gm, '')
      .replace(/Page \d+ of \d+\s*\n?/g, '')
      .replace(/^(Job Description\s*\n?){2,}/gm, '')
      .replace(/^Job Description\s*\n?/gm, '')
      .replace(/^Job Description$/gm, '')
      .replace(/Job Description\s*\n?/g, '')
      .replace(/^([A-Z][A-Z\s]+:)/gm, (match, _label, offset, full) => {
        const lineEnd = full.indexOf('\n', offset);
        const line = full.slice(offset, lineEnd === -1 ? full.length : lineEnd).trim();
        if (isJdMetaLine(line)) return match;
        return `\n\n${match}`;
      })
      .replace(/(\b(EDUCATION|SKILLS|Work Experience|Position Summary|Essential Functions|About|Non-Negotiables|Psychometric|Key Performance):)/g, '\n\n$1')
      .replace(/(\n\s*-\s*)/g, '\n• ')
      .replace(/(\n\s*\d+\.\s*)/g, '\n$1')
      .replace(/([.!?])\s+([A-Z][a-z])/g, '$1\n\n$2')
      .replace(/\n{3,}/g, '\n\n');
    return normalizeJdExperienceZeroToEntryLevel(out).trim();
  };

  const redirectNoJdAvailable = () => {
    toast.info('No job description is available for this position.');
    if (asModal) {
      onClose?.();
      return;
    }
    navigate('/existing-positions', { replace: true });
  };

  const handleBackNavigation = () => {
    if (asModal) {
      onClose?.();
      return;
    }
    const returnTarget = resolveJdPreviewReturnTarget(location);
    const nextState = returnTarget.state ? { ...returnTarget.state } : undefined;

    if (nextState?.fromJdPreview) {
      // positionId from useParams() is always a string; "null" means no position yet — keep it as null
      const resolvedPositionId = (positionId && positionId !== 'null') ? positionId : (location.state?.positionId ?? null);
      nextState.positionId = resolvedPositionId;
      nextState.positionName = location.state?.positionName || nextState.positionName || '';
      nextState.clientName = location.state?.clientName || nextState.clientName || '';
      nextState.jdType = location.state?.jdType || nextState.jdType || null;
      nextState.jdContent = editedJd || location.state?.jdContent || nextState.jdContent || '';
    }

    navigate(
      {
        pathname: returnTarget.pathname,
        search: returnTarget.search || '',
      },
      nextState ? { state: nextState } : undefined
    );
  };

  useEffect(() => {
    // Check if we have data passed from OpenPositions page
    if (!asModal && location.state && location.state.jdContent) {
      debugLog('Received JD content from OpenPositions:', location.state);
      setEditedJd(formatJdText(location.state.jdContent));
      setLoading(false);
      
      // Create mock jdData structure for the passed content
      const mockJdData = {
        aiJd: location.state.jdType === 'ai' ? {
          jdText: location.state.jdContent,
          version: 1,
          createdAt: new Date().toISOString(),
          isAiGenerated: true
        } : null,
        editedJds: location.state.jdType === 'upload' ? [{
          jdId: Date.now(),
          jdText: location.state.jdContent,
          version: 1,
          createdAt: new Date().toISOString(),
          isAiGenerated: false,
          sourceType: 'FILE_UPLOAD'
        }] : []
      };
      setJdData(mockJdData);
    } else {
      fetchJdData();
    }
  }, [positionId, location.state]);

  const fetchJdData = async () => {
    try {
      debugLog('Fetching JD data for position:', positionId);
      
      // First, check if backend is running by trying to fetch positions
      try {
        await api.get('/positions');
        debugLog('Backend is running');
      } catch (backendError) {
        console.error('Backend connection error:', backendError);
        if (backendError.code === 'ECONNREFUSED' || backendError.message.includes('Network Error')) {
          toast.error('Backend server is not running. Please start the backend server.');
          setLoading(false);
          return;
        }
      }
      
      // Fetch AI JD and edited JDs separately to handle 404s gracefully
      let aiJd = null;
      let editedJds = [];
      
      try {
        const aiJdResponse = await api.get(`/job-descriptions/position/${positionId}/ai`);
        aiJd = aiJdResponse.data;
        debugLog('AI JD response:', aiJd);
      } catch (aiError) {
        if (aiError.response?.status !== 404) {
          console.error('Unexpected error while fetching AI JD:', aiError);
        }
      }
      
      try {
        const editedJdsResponse = await api.get(`/job-descriptions/position/${positionId}/edited`);
        editedJds = editedJdsResponse.data || [];
        debugLog('Edited JDs response:', editedJds);
      } catch (editedError) {
        if (editedError.response?.status !== 404) {
          console.error('Unexpected error while fetching edited JDs:', editedError);
        }
      }

      const hasAnyJd = Boolean(aiJd) || (Array.isArray(editedJds) && editedJds.length > 0);
      if (!hasAnyJd) {
        redirectNoJdAvailable();
        return;
      }

      setJdData({
        aiJd: aiJd,
        editedJds: editedJds
      });

      // Set the latest JD as the current content
      // Backend returns edited JDs ORDER BY version DESC (newest first), so index 0 is latest
      if (editedJds && editedJds.length > 0) {
        const latestEdited = editedJds[0];
        debugLog('Latest edited/uploaded JD from server:', latestEdited);
        const cleanedText = latestEdited.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        const formattedText = formatJdText(cleanedText);
        
        // Only update if the content is different from what we currently have
        if (formattedText !== editedJd) {
          setEditedJd(formattedText);
          debugLog('Updated editedJd to new content:', formattedText.substring(0, 100) + '...');
        } else {
          debugLog('Content is the same, not updating editedJd');
        }
      } else if (aiJd) {
        debugLog('No edited/uploaded JDs, using AI JD:', aiJd);
        const cleanedText = aiJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        const formattedText = formatJdText(cleanedText);
        
        // Only update if the content is different from what we currently have
        if (formattedText !== editedJd) {
          setEditedJd(formattedText);
          debugLog('Updated editedJd to AI JD:', formattedText.substring(0, 100) + '...');
        } else {
          debugLog('AI JD content is the same, not updating editedJd');
        }
      } else {
        // Defensive fallback (normally covered by hasAnyJd check above).
        redirectNoJdAvailable();
        return;
      }

    } catch (error) {
      console.error('Error fetching JD data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Check if it's a network error (backend not running)
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        toast.error('Backend server is not running. Please start the backend server.');
      } else if (error.response?.status === 404) {
        // No JD exists for this position: don't keep user on preview layout.
        redirectNoJdAvailable();
        return;
      } else {
        toast.error('Failed to load job description data: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEditedJd = async () => {
    if (!editedJd.trim()) {
      toast.error('Please enter some content');
      return;
    }

    const cleanedJd = editedJd.replace(/^"|"$/g, '').replace(/\\n/g, '\n');

    // Coming from the create-new flow (OpenPositions) — position not yet in DB.
    // Save in memory only; the actual DB write happens when the user clicks "Attach JD".
    if (location.state?.fromOpenPositions) {
      setEditedJd(cleanedJd);
      setIsEditing(false);
      setViewingAiJd(false);
      toast.success('Changes saved. Click "Attach JD" to commit to the position.');
      return;
    }

    // Existing position — safe to write to DB.
    setSaving(true);
    try {
      debugLog('Saving cleaned JD:', cleanedJd.substring(0, 100) + '...');

      await api.post(`/job-descriptions/position/${positionId}/save-edited`,
        cleanedJd,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Edited job description saved successfully!');
      setIsEditing(false);
      setViewingAiJd(false);
      onSaved?.();

      if (jdData) {
        const updatedJdData = { ...jdData };
        const newEditedJd = {
          jdId: Date.now(),
          version: (jdData.editedJds ? jdData.editedJds.length + 1 : 1),
          jdText: cleanedJd,
          createdAt: new Date().toISOString(),
          isAiGenerated: false
        };
        if (updatedJdData.editedJds) {
          updatedJdData.editedJds.unshift(newEditedJd);
        } else {
          updatedJdData.editedJds = [newEditedJd];
        }
        setJdData(updatedJdData);
      }
    } catch (error) {
      toast.error('Failed to save edited job description');
      console.error('Error saving edited JD:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (exportBlocked()) return;
    if (downloadingPdf || downloadingWord) return;
    setDownloadingPdf(true);
    try {
      const jdContent = editedJd || '';
      if (!jdContent.trim()) {
        toast.error('No job description content to download.');
        return;
      }

      const logoData = logoDataRef.current || await loadFeujiLogoData();
      if (logoData) logoDataRef.current = logoData;

      const doc = await renderJdRichHtmlToPdf(jdContent, logoData);
      doc.save('job-description.pdf');
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleViewAiJd = () => {
    if (jdData && jdData.aiJd) {
      const cleanedText = jdData.aiJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
      setEditedJd(formatJdText(cleanedText));
      setViewingAiJd(true);
      setViewingVersion('ai');
      setIsEditing(false);
    }
  };

  const handleViewEditedVersion = (versionIndex) => {
    if (jdData && jdData.editedJds && jdData.editedJds[versionIndex]) {
      const cleanedText = jdData.editedJds[versionIndex].jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
      setEditedJd(formatJdText(cleanedText));
      setViewingAiJd(false);
      setViewingVersion(`edited-${versionIndex}`);
      setIsEditing(false);
    }
  };

  const handleBackToLatest = () => {
    if (jdData) {
      if (jdData.editedJds && jdData.editedJds.length > 0) {
        const latestJd = jdData.editedJds[0]; // Backend returns ORDER BY version DESC (newest first)
        const cleanedText = latestJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        setEditedJd(formatJdText(cleanedText));
        debugLog('Back to latest JD (edited/uploaded):', cleanedText.substring(0, 100) + '...');
      } else if (jdData.aiJd) {
        const cleanedText = jdData.aiJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        setEditedJd(formatJdText(cleanedText));
        debugLog('Back to AI JD:', cleanedText.substring(0, 100) + '...');
      }
      setViewingAiJd(false);
      setViewingVersion(null);
    }
  };

  /** Revert to last saved content and exit edit mode (used when Cancel is clicked). */
  const handleCancelEdit = () => {
    if (jdData) {
      if (jdData.editedJds && jdData.editedJds.length > 0) {
        const latestJd = jdData.editedJds[0];
        const cleanedText = latestJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        setEditedJd(formatJdText(cleanedText));
      } else if (jdData.aiJd) {
        const cleanedText = jdData.aiJd.jdText.replace(/^"|"$/g, '').replace(/\\n/g, '\n');
        setEditedJd(formatJdText(cleanedText));
      }
    }
    setIsEditing(false);
  };

  const handleRenumberVersions = async () => {
    try {
      await api.post(`/job-descriptions/position/${positionId}/renumber-versions`);
      toast.success('Versions renumbered successfully!');
      fetchJdData(); // Refresh data to show corrected versions
    } catch (error) {
      toast.error('Failed to renumber versions');
      console.error('Error renumbering versions:', error);
    }
  };

  const handleDownloadWord = async () => {
    if (exportBlocked()) return;
    if (downloadingPdf || downloadingWord) return;
    setDownloadingWord(true);
    try {
      const jdContent = editedJd || '';
      if (!jdContent.trim()) {
        toast.error('No job description content to download.');
        return;
      }

      const logoData = logoDataRef.current || await loadFeujiLogoData();
      if (logoData) logoDataRef.current = logoData;

      const blob = await renderJdToDocxBlob(jdContent, logoData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'job-description.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download Word document. Please try again.');
      console.error('Error downloading Word:', error);
    } finally {
      setDownloadingWord(false);
    }
  };

  const wrapView = (children, { center = false } = {}) => {
    const inner = (
      <div style={{
        minHeight: asModal ? (center ? 200 : 0) : '100vh',
        display: center ? 'flex' : (asModal ? 'flex' : undefined),
        flexDirection: asModal && !center ? 'column' : undefined,
        alignItems: center ? 'center' : undefined,
        justifyContent: center ? 'center' : undefined,
        background: 'var(--ds-bg)',
        padding: asModal ? 12 : '20px 0',
        fontFamily: "'Plus Jakarta Sans','Inter',sans-serif",
        boxSizing: 'border-box',
        minWidth: 0,
        width: asModal ? '100%' : undefined,
        height: asModal && !center ? '100%' : undefined,
        overflow: asModal ? 'auto' : undefined,
      }}>
        {children}
      </div>
    );
    if (!asModal) return inner;
    return createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 4000,
          overflowY: 'auto',
          padding: 20,
          boxSizing: 'border-box',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div
          style={{
            background: 'var(--ds-bg)',
            borderRadius: 12,
            width: '100%',
            maxWidth: 1100,
            maxHeight: 'calc(100vh - 40px)',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.28)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {inner}
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return wrapView((
      <>
        <style>{`@keyframes jdp-spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--ds-border)', borderTop: '3px solid #1A5FD4', borderRadius: '50%', animation: 'jdp-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--ds-muted)', fontSize: 14 }}>Loading job description...</p>
        </div>
      </>
    ), { center: true });
  }

  if (!jdData) {
    return wrapView((
      <div style={{ background: 'var(--ds-card)', borderRadius: 8, padding: '20px 24px', border: '1px solid #f5c6cb', color: '#721c24', maxWidth: 400 }}>
        <strong>Error</strong>
        <p style={{ margin: '8px 0 0', fontSize: 14 }}>Failed to load job description data.</p>
      </div>
    ), { center: true });
  }

  return wrapView((
    <>
      <style>{`@keyframes jdp-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: asModal ? 0 : '0 16px', minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
        {/* Header Section */}
        {asModal ? (
          <div style={{ marginBottom: 10 }}>
            <div style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)' }}>
              <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 700, color: 'white', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayPositionName ? `JD Preview — ${displayPositionName}` : 'JD Preview'}
                </span>
                <button
                  onClick={handleBackNavigation}
                  style={{ borderRadius: 6, padding: '4px 10px', fontWeight: 600, border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
                >
                  <i className="fas fa-times" style={{ marginRight: 6 }}></i>Close
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div style={{ marginBottom: 12 }}>
          <div style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', border: 'none', padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ marginRight: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-eye" style={{ fontSize: '0.9rem', color: 'white' }}></i>
                    </div>
                  </div>
                  <div>
                    <h5 style={{ margin: '0 0 2px', color: 'white', fontWeight: 700 }}>JD Preview</h5>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                      {displayPositionName ?
                        `Review and edit the job description for ${displayPositionName}` :
                        'Review and edit the generated job description'
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleBackNavigation}
                  style={{ borderRadius: 6, padding: '6px 12px', fontWeight: 600, border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                >
                  <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i>
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Main Content */}
        <div style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          flexWrap: asModal ? 'nowrap' : 'wrap',
          minWidth: 0,
        }}>
          {/* JD Content Card — takes most of the width */}
          <div style={{
            flex: '1 1 0',
            minWidth: 0,
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--ds-card)',
            boxShadow: 'var(--ds-shadow-md)',
          }}>
            <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ marginRight: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-file-alt" style={{ fontSize: '0.9rem', color: 'white' }}></i>
                    </div>
                  </div>
                  <div>
                    <h5 style={{ margin: '0 0 2px', color: 'white', fontWeight: 700 }}>Job Description Content</h5>
                    {viewingVersion && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <small style={{ color: '#ffc107', display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
                          <i className="fas fa-eye" style={{ marginRight: 4 }}></i>
                          {viewingVersion === 'ai' ? `Viewing ${getJdSourceTypeLabel(jdData?.aiJd)} JD (v${jdData?.aiJd?.version || 1})` :
                           viewingVersion.startsWith('edited-') ?
                           `Viewing ${getJdSourceTypeLabel(jdData?.editedJds?.[parseInt(viewingVersion.split('-')[1])])} Version (v${jdData?.editedJds?.[parseInt(viewingVersion.split('-')[1])]?.version || ''})` :
                           'Viewing specific version'}
                        </small>
                        <button
                          onClick={handleBackToLatest}
                          title="Back to latest version"
                          style={{ borderRadius: 6, padding: '4px 8px', fontSize: '0.7rem', border: '1px solid #ffc107', color: '#ffc107', background: 'transparent', cursor: 'pointer' }}
                        >
                          <i className="fas fa-arrow-left" style={{ marginRight: 4 }}></i>
                          Back to Latest
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      style={{ borderRadius: 6, padding: '6px 12px', fontWeight: 600, border: 'none', background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.3s ease' }}
                      onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.25)'; }}
                      onMouseLeave={(e) => { e.target.style.background = 'rgba(255,255,255,0.15)'; }}
                    >
                      <i className="fas fa-edit" style={{ marginRight: 6 }}></i>
                      Edit JD
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handleSaveEditedJd}
                        disabled={saving}
                        style={{ borderRadius: 6, padding: '6px 12px', fontWeight: 600, border: 'none', background: 'var(--feuji-green, #28a745)', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        {saving ? (
                          <>
                            <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'jdp-spin 0.8s linear infinite' }} />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{ borderRadius: 6, padding: '6px 12px', fontWeight: 600, border: '1px solid rgba(255,255,255,0.4)', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                        onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.15)'; }}
                        onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
                      >
                        <i className="fas fa-times" style={{ marginRight: 6 }}></i>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              {isEditing ? (
                <textarea
                  rows={asModal ? 10 : 15}
                  value={editedJd || ''}
                  onChange={(e) => setEditedJd(e.target.value)}
                  style={{
                    minWidth: 0,
                    maxWidth: '100%',
                    width: '100%',
                    overflow: 'auto',
                    fontFamily: "'Plus Jakarta Sans','Inter',sans-serif",
                    fontSize: '0.85rem',
                    borderRadius: 6,
                    border: '2px solid var(--ds-border)',
                    padding: 12,
                    transition: 'all 0.3s ease',
                    resize: asModal ? 'none' : 'vertical',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    background: 'var(--ds-input-bg, var(--ds-surface))',
                    color: 'var(--ds-text)',
                    boxSizing: 'border-box',
                    maxHeight: asModal ? 300 : undefined,
                    height: asModal ? 280 : undefined,
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#1A5FD4'; e.target.style.outline = 'none'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--ds-border)'; }}
                />
              ) : (
                <>
                  <style>{JD_PREVIEW_INNER_CSS}</style>
                  <div
                    className="jd-preview-content"
                    style={{
                      fontFamily: "'Plus Jakarta Sans','Inter',sans-serif",
                      fontSize: '0.85rem',
                      lineHeight: '1.5',
                      padding: 12,
                      background: 'var(--ds-surface)',
                      borderRadius: 6,
                      border: '1px solid var(--ds-border)',
                      height: asModal ? 300 : 'calc(100vh - 250px)',
                      overflowY: 'auto',
                      color: 'var(--ds-text)',
                      textAlign: 'left',
                    }}
                    dangerouslySetInnerHTML={{ __html: formatJdPreviewHtml(editedJd) }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div style={{ flex: '0 0 260px', width: 260, minWidth: 260, maxWidth: 260, flexShrink: 0 }}>
            {/* Download Options Card */}
            <div style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)', marginBottom: 12 }}>
              <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ marginRight: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-download" style={{ fontSize: '0.9rem', color: 'white' }}></i>
                    </div>
                  </div>
                  <h6 style={{ margin: 0, color: 'white', fontWeight: 700 }}>Download Options</h6>
                </div>
              </div>
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloadingPdf || downloadingWord}
                  style={{ borderRadius: 4, padding: '6px 10px', fontWeight: 600, border: 'none', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: 'white', cursor: (downloadingPdf || downloadingWord) ? 'not-allowed' : 'pointer', fontSize: '0.75rem', transition: 'all 0.3s ease', width: '100%', opacity: (downloadingPdf || downloadingWord) ? 0.65 : 1 }}
                  onMouseEnter={(e) => { if (!downloadingPdf && !downloadingWord) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 25px rgba(0,65,194,0.35)'; } }}
                  onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                >
                  <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>
                  {downloadingPdf ? 'Generating PDF…' : 'Download PDF'}
                </button>
                <button
                  onClick={handleDownloadWord}
                  disabled={downloadingPdf || downloadingWord}
                  style={{ borderRadius: 4, padding: '6px 10px', fontWeight: 600, border: 'none', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: 'white', cursor: (downloadingPdf || downloadingWord) ? 'not-allowed' : 'pointer', fontSize: '0.75rem', transition: 'all 0.3s ease', width: '100%', opacity: (downloadingPdf || downloadingWord) ? 0.65 : 1 }}
                  onMouseEnter={(e) => { if (!downloadingPdf && !downloadingWord) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 25px rgba(0,65,194,0.35)'; } }}
                  onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                >
                  <i className="fas fa-file-word" style={{ marginRight: 6 }}></i>
                  {downloadingWord ? 'Generating Word…' : 'Download Word'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  ));
};

export default JdPreview;
